import { useMemo, useRef, useSyncExternalStore } from 'react';
import { useUserRepository } from './rpc';
import logger from '@ayocore/platform/common/helpers/logger';

/**
 * Кешированные данные пользователя.
 * Содержит всё необходимое для отображения в ячейках таблиц и других компонентах.
 */
export interface CachedUser {
  id: number;
  name: string;
  username: string | null;
  email: string | null;
  avatarThumbUrl: string | null;
}

interface UsersState {
  users: Map<number, CachedUser>;
  isLoading: boolean;
  isLoaded: boolean;
}

/**
 * UsersController — синглтон-кеш всех пользователей системы.
 *
 * При первом обращении загружает всех юзеров одним SQL-запросом
 * (с avatar URL через withAvatarUrl JOIN) и хранит в `Map<id, CachedUser>`.
 *
 * Паттерн: Controller + useSyncExternalStore (как AuthController, NotificationController).
 */
export class UsersController {
  private static instance: UsersController;
  private listeners = new Set<() => void>();
  private state: UsersState = {
    users: new Map(),
    isLoading: false,
    isLoaded: false,
  };

  private constructor() {
    // Загружаем юзеров сразу при создании контроллера
    void this.loadAll();
  }

  static getInstance(): UsersController {
    if (!UsersController.instance) {
      UsersController.instance = new UsersController();
    }
    return UsersController.instance;
  }

  // ===== useSyncExternalStore API =====

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UsersState => this.state;

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private replaceState(next: Partial<UsersState>): void {
    this.state = { ...this.state, ...next };
    this.emit();
  }

  // ===== Загрузка данных =====

  /**
   * Загружает всех юзеров одним SQL-запросом.
   * avatar_thumb_url резолвится на уровне SQL через withAvatarUrl() (JOIN с filemanager).
   */
  private async loadAll(): Promise<void> {
    try {
      this.replaceState({ isLoading: true });

      const rows = await useUserRepository().withAvatarUrl().fetch();

      // Строим Map<id, CachedUser>
      const users = new Map<number, CachedUser>();
      for (const row of rows) {
        users.set(row.id, {
          id: row.id,
          name: row.name,
          username: row.username ?? null,
          email: row.email ?? null,
          avatarThumbUrl:
            ((row as Record<string, unknown>).avatar_thumb_url as string | null) ?? null,
        });
      }

      this.replaceState({ users, isLoading: false, isLoaded: true });
      logger.info(`[UsersController] Loaded ${users.size} users`);
    } catch (error) {
      logger.error('[UsersController] loadAll error:', error);
      this.replaceState({ isLoading: false });
    }
  }

  // ===== Публичный API =====

  /** Получить пользователя по ID из кеша */
  getUser(id: number): CachedUser | undefined {
    return this.state.users.get(id);
  }

  /** Принудительно перезагрузить кеш */
  async reload(): Promise<void> {
    this.replaceState({ isLoaded: false });
    await this.loadAll();
  }
}

// ===== React-хук =====

type FullState = ReturnType<typeof withActions>;

/**
 * useUsersController — реактивный доступ к кешу пользователей.
 *
 * @example
 * const { getUser } = useUsersController();
 * const cached = getUser(userId);
 *
 * @example
 * const isLoaded = useUsersController(s => s.isLoaded);
 */
export function useUsersController(): FullState;
export function useUsersController<S>(selector: (full: FullState) => S): S;
export function useUsersController<S = FullState>(
  selector?: (full: FullState) => S
): S | FullState {
  const controller = UsersController.getInstance();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  const full = useMemo(() => withActions(controller, state), [controller, state]);

  if (!selector) return full;

  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}

function withActions(controller: UsersController, snapshot: UsersState) {
  return {
    // State
    users: snapshot.users,
    isLoading: snapshot.isLoading,
    isLoaded: snapshot.isLoaded,
    // Actions
    getUser: controller.getUser.bind(controller),
    reload: controller.reload.bind(controller),
  };
}
